// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Shield,
  Zap,
  Heart,
  Swords,
  Skull,
  ArrowRight,
  HelpCircle,
  RefreshCcw,
  Settings,
  BookOpen,
  X,
  Trophy,
} from "lucide-react";

// Firebase Imports
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithCustomToken,
  signInAnonymously,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

// 簡易 CSV 解析工具
const parseCSV = (str) => {
  const arr = [];
  let quote = false;
  for (let row = 0, col = 0, c = 0; c < str.length; c++) {
    let cc = str[c],
      nc = str[c + 1];
    arr[row] = arr[row] || [];
    arr[row][col] = arr[row][col] || "";
    if (cc === '"' && quote && nc === '"') {
      arr[row][col] += cc;
      ++c;
      continue;
    }
    if (cc === '"') {
      quote = !quote;
      continue;
    }
    if (cc === "," && !quote) {
      ++col;
      continue;
    }
    if (cc === "\r" && nc === "\n" && !quote) {
      ++row;
      col = 0;
      ++c;
      continue;
    }
    if (cc === "\n" && !quote) {
      ++row;
      col = 0;
      continue;
    }
    if (cc === "\r" && !quote) {
      ++row;
      col = 0;
      continue;
    }
    arr[row][col] += cc;
  }
  if (arr.length === 0) return [];
  const headers = arr[0].map((h) => h.trim());
  return arr
    .slice(1)
    .filter((row) => row.length > 0 && row[0] !== "")
    .map((row) => {
      let obj = {};
      headers.forEach((h, i) => (obj[h] = row[i] ? row[i].trim() : ""));
      return obj;
    });
};

const BASE_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vSzQ-Hff06SMZ9XooHut4PhgrVvcdGKXnkPDBq7v-vjbldq9dV6hwBzgn8_C2pimA/pub?output=csv";

const SHEET_URLS = {
  cards: `${BASE_URL}&gid=823261687`,
  playerDeck: `${BASE_URL}&gid=1018653615`,
  questions: {
    初級: `${BASE_URL}&gid=2009818818`,
    中級: `${BASE_URL}&gid=1130596736`,
    中高級: `${BASE_URL}&gid=1155624727`,
    高級: `${BASE_URL}&gid=1704845946`,
  },
};

const ENEMY_LIST = [
  { id: "E1", name: "史萊姆小兵", hp: 20, maxHp: 20, atk: 3, type: "minion" },
  { id: "E2", name: "殭屍戰士", hp: 40, maxHp: 40, atk: 5, type: "minion" },
  { id: "E3", name: "獸人苦工", hp: 60, maxHp: 60, atk: 8, type: "minion" },
  { id: "E4", name: "暗黑騎士", hp: 80, maxHp: 80, atk: 12, type: "minion" },
  {
    id: "E5",
    name: "遠古惡龍 (魔王)",
    hp: 200,
    maxHp: 200,
    atk: 20,
    type: "boss",
  },
];

const LEVEL_UP_POOL = [
  {
    id: 1,
    title: "攻擊力 +3",
    desc: "影響所有攻擊牌",
    icon: Swords,
    color: "text-red-400",
    border: "hover:border-red-500",
    bg: "bg-red-500/20 group-hover:bg-red-500",
  },
  {
    id: 2,
    title: "護盾 +3",
    desc: "每回合開始自動附加",
    icon: Shield,
    color: "text-blue-400",
    border: "hover:border-blue-500",
    bg: "bg-blue-500/20 group-hover:bg-blue-500",
  },
  {
    id: 3,
    title: "法力最大值 +1",
    desc: "每回合回復滿，立即生效",
    icon: Zap,
    color: "text-purple-400",
    border: "hover:border-purple-500",
    bg: "bg-purple-500/20 group-hover:bg-purple-500",
  },
  {
    id: 4,
    title: "抽牌數 +1",
    desc: "每回合額外抽 1 張牌",
    icon: ArrowRight,
    color: "text-green-400",
    border: "hover:border-green-500",
    bg: "bg-green-500/20 group-hover:bg-green-500",
  },
  {
    id: 5,
    title: "連擊增傷",
    desc: "每連續答對1題傷害+5%(可疊加)，答錯重置",
    icon: Swords,
    color: "text-yellow-400",
    border: "hover:border-yellow-500",
    bg: "bg-yellow-500/20 group-hover:bg-yellow-500",
  },
  {
    id: 6,
    title: "攻擊轉盾",
    desc: "每成功發動一次攻擊，護盾+2(可疊加)",
    icon: Shield,
    color: "text-cyan-400",
    border: "hover:border-cyan-500",
    bg: "bg-cyan-500/20 group-hover:bg-cyan-500",
  },
  {
    id: 7,
    title: "復仇打擊",
    desc: "攻擊失敗時，下次攻擊傷害+50%(可疊加)",
    icon: Skull,
    color: "text-rose-400",
    border: "hover:border-rose-500",
    bg: "bg-rose-500/20 group-hover:bg-rose-500",
  },
  {
    id: 8,
    title: "進階卡牌",
    desc: "從隨機 5 張中高級/高級卡牌中挑選 2 張",
    icon: RefreshCcw,
    color: "text-indigo-400",
    border: "hover:border-indigo-500",
    bg: "bg-indigo-500/20 group-hover:bg-indigo-500",
  },
  {
    id: 9,
    title: "精簡牌庫",
    desc: "自選：消除牌庫中最多 2 張牌",
    icon: X,
    color: "text-slate-400",
    border: "hover:border-slate-400",
    bg: "bg-slate-500/20 group-hover:bg-slate-500",
  },
];

const SUPPLY_OPTIONS_POOL = [
  {
    id: 1,
    title: "恢復生命",
    desc: "恢復 50% 最大生命值",
    icon: Heart,
    color: "text-green-400",
    border: "border-green-600",
    bg: "bg-green-500/20",
  },
  {
    id: 2,
    title: "研讀秘卷",
    desc: "自選 2 張「高級/中高級」卡牌",
    icon: BookOpen,
    color: "text-purple-400",
    border: "border-purple-600",
    bg: "bg-purple-500/20",
  },
  {
    id: 3,
    title: "精簡牌庫",
    desc: "移除最多 3 張牌庫或手牌中的卡牌",
    icon: X,
    color: "text-red-400",
    border: "border-red-600",
    bg: "bg-red-500/20",
  },
  {
    id: 4,
    title: "突破極限",
    desc: "立刻直升 1 級，獲取升級獎勵",
    icon: Zap,
    color: "text-yellow-400",
    border: "border-yellow-600",
    bg: "bg-yellow-500/20",
  },
  {
    id: 5,
    title: "附加職業",
    desc: "隨機獲得另一種職業的被動天賦",
    icon: Shield,
    color: "text-blue-400",
    border: "border-blue-600",
    bg: "bg-blue-500/20",
  },
];

// ============================================================================
// 👇👇👇 你的專屬 Firebase 設定區 👇👇👇
// ============================================================================
const MY_FIREBASE_CONFIG = {
  apiKey: "AIzaSyA2UBPGu7RkhrstaiT9WmylO_uUV-_VPFw",
  authDomain: "power-card-game.firebaseapp.com",
  projectId: "power-card-game",
  storageBucket: "power-card-game.firebasestorage.app",
  messagingSenderId: "470129469566",
  appId: "1:470129469566:web:30d82e3378506cc3e2902f",
};

const hasMyConfig = Object.keys(MY_FIREBASE_CONFIG).length > 0;
const hasCloudDB = hasMyConfig || typeof __firebase_config !== "undefined";
const firebaseConfig = hasMyConfig
  ? MY_FIREBASE_CONFIG
  : typeof __firebase_config !== "undefined"
  ? JSON.parse(__firebase_config)
  : null;

let app, auth, db;
if (hasCloudDB) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
}
const appId = typeof __app_id !== "undefined" ? __app_id : "local-app-id";

const getLeaderboardRef = () => {
  if (hasMyConfig) return collection(db, "global_leaderboard");
  return collection(db, "artifacts", appId, "public", "data", "leaderboard");
};

// ============================================================================
// 主程式元件
// ============================================================================
export default function App() {
  const [gameState, setGameState] = useState("LOADING");
  const [isTutorial, setIsTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [difficulty, setDifficulty] = useState("normal");

  const [currentActionLogs, setCurrentActionLogs] = useState([]);
  const [pendingNextState, setPendingNextState] = useState("");

  const [cardLibrary, setCardLibrary] = useState({});
  const [initialDeckConfig, setInitialDeckConfig] = useState([]);
  const [questionBank, setQuestionBank] = useState({});

  const [deckBuilderState, setDeckBuilderState] = useState({});
  const [deckBuilderError, setDeckBuilderError] = useState("");

  const [rewardCards, setRewardCards] = useState([]);
  const [selectedRewardCards, setSelectedRewardCards] = useState([]);
  const [levelUpOptions, setLevelUpOptions] = useState([]);
  const [cardRewardContext, setCardRewardContext] = useState("battle");
  const [removalSelected, setRemovalSelected] = useState([]);
  const [removalLimit, setRemovalLimit] = useState(2);

  const [hasVisitedSupply, setHasVisitedSupply] = useState(false);
  const [supplyOptions, setSupplyOptions] = useState([]);
  const [selectedSupplyOptions, setSelectedSupplyOptions] = useState([]);
  const [supplyQueue, setSupplyQueue] = useState([]);

  const [user, setUser] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardTab, setLeaderboardTab] = useState("normal");
  const [playerNameInput, setPlayerNameInput] = useState("");
  const [hasSubmittedScore, setHasSubmittedScore] = useState(false);
  const [showAdminClear, setShowAdminClear] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [cloudError, setCloudError] = useState("");
  const isCloudMode = hasCloudDB && !cloudError;

  useEffect(() => {
    if (!hasCloudDB) {
      setUser({ uid: "local-user" });
      return;
    }

    const initAuth = async () => {
      try {
        if (hasMyConfig) {
          await signInAnonymously(auth);
        } else if (
          typeof __initial_auth_token !== "undefined" &&
          __initial_auth_token
        ) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.error("Auth error:", error);
        setCloudError("Firebase 驗證失敗 (請確認後台是否已啟用「匿名登入」)");
        setUser({ uid: "local-fallback" });
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (u) setUser(u);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    if (isCloudMode) {
      const leaderboardRef = getLeaderboardRef();
      const unsubscribe = onSnapshot(
        leaderboardRef,
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          data.sort((a, b) => {
            if (b.level !== a.level) return b.level - a.level;
            return b.timestamp - a.timestamp;
          });
          setLeaderboard(data);
        },
        (err) => {
          console.error("Firestore error:", err);
          setCloudError("雲端讀取失敗 (請確認 Firestore 規則設定)");
        }
      );
      return () => unsubscribe();
    } else {
      const localData = JSON.parse(
        localStorage.getItem("knowledge_guardian_leaderboard") || "[]"
      );
      localData.sort((a, b) => {
        if (b.level !== a.level) return b.level - a.level;
        return b.timestamp - a.timestamp;
      });
      setLeaderboard(localData);
    }
  }, [user, isCloudMode]);

  const [playerClass, setPlayerClass] = useState("戰士");
  const [player, setPlayer] = useState({
    hp: 60,
    maxHp: 60,
    mana: 3,
    maxMana: 3,
    shield: 0,
    atkBonus: 0,
    baseShield: 0,
    drawCount: 4,
    level: 1,
    correctCount: 0,
    pendingCardReward: false,
    comboCount: 0,
    revengeActive: false,
    passives: {},
    extraClasses: [],
  });
  const [deck, setDeck] = useState([]);
  const [hand, setHand] = useState([]);
  const [discardPile, setDiscardPile] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [currentEnemyIdx, setCurrentEnemyIdx] = useState(0);

  const [activeCard, setActiveCard] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  const [enemyHitState, setEnemyHitState] = useState(false);
  const [playerHitState, setPlayerHitState] = useState(false);
  const [floatingTexts, setFloatingTexts] = useState([]);

  const hasClass = (clsName) => {
    return (
      playerClass === clsName ||
      (player.extraClasses && player.extraClasses.includes(clsName))
    );
  };

  const getEnemyAvatar = (id) => {
    switch (id) {
      case "E0":
        return "🪵";
      case "E1":
        return "💧";
      case "E2":
        return "🧟";
      case "E3":
        return "👹";
      case "E4":
        return "♞";
      case "E5":
        return "🐉";
      default:
        return "👾";
    }
  };

  useEffect(() => {
    if (!document.getElementById("tailwind-cdn")) {
      const script = document.createElement("script");
      script.id = "tailwind-cdn";
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }

    const loadData = async () => {
      try {
        let loadedCards = {};
        if (SHEET_URLS.cards) {
          const res = await fetch(SHEET_URLS.cards);
          const csvText = await res.text();
          const parsedData = parseCSV(csvText);
          parsedData.forEach((row) => {
            if (row.id || row["卡牌 ID"]) {
              const id = row.id || row["卡牌 ID"];
              loadedCards[id] = {
                ...row,
                id: id,
                name: row.name || row["卡牌名稱"] || "未命名",
                tier: row.tier || row["等級"] || "初級",
                type: row.type || row["類型"] || "攻擊",
                desc: row.desc || row["卡牌描述"] || "",
                cost: parseInt(row.cost || row["需求法力"] || 0),
                value: parseInt(row.value || row["效果數值"] || 0),
              };
            }
          });
        }
        setCardLibrary(loadedCards);

        let loadedDeckConfig = [];
        if (SHEET_URLS.playerDeck) {
          const res = await fetch(SHEET_URLS.playerDeck);
          const csvText = await res.text();
          const parsedData = parseCSV(csvText);
          loadedDeckConfig = parsedData
            .filter((row) => row.cardId || row["卡牌 ID"])
            .map((row) => ({
              cardId: row.cardId || row["卡牌 ID"],
              count: parseInt(row.count || row["數量"] || 0),
            }))
            .filter((config) => {
              const card = loadedCards[config.cardId];
              return card && (card.tier === "初級" || card.tier === "中級");
            });
        }
        setInitialDeckConfig(loadedDeckConfig);

        let loadedQuestions = {};
        for (const [tier, url] of Object.entries(SHEET_URLS.questions)) {
          if (url) {
            try {
              const res = await fetch(url);
              const csvText = await res.text();
              const parsedData = parseCSV(csvText);

              const formattedQuestions = parsedData
                .filter((row) => row.id || row["題目 ID"] || row["題目"])
                .map((row) => {
                  const options = [
                    row["選項1"] ||
                      row["選項 A"] ||
                      row["選項A"] ||
                      row["optionA"] ||
                      row["A"],
                    row["選項2"] ||
                      row["選項 B"] ||
                      row["選項B"] ||
                      row["optionB"] ||
                      row["B"],
                    row["選項3"] ||
                      row["選項 C"] ||
                      row["選項C"] ||
                      row["optionC"] ||
                      row["C"],
                    row["選項4"] ||
                      row["選項 D"] ||
                      row["選項D"] ||
                      row["optionD"] ||
                      row["D"],
                  ].filter(Boolean);

                  let ans =
                    row["答案"] ||
                    row["正確解答"] ||
                    row["answer"] ||
                    row["Answer"];

                  if (ans) {
                    const upperAns = String(ans).toUpperCase().trim();
                    if (upperAns === "A" || upperAns === "1") ans = options[0];
                    if (upperAns === "B" || upperAns === "2") ans = options[1];
                    if (upperAns === "C" || upperAns === "3") ans = options[2];
                    if (upperAns === "D" || upperAns === "4") ans = options[3];
                  }

                  return {
                    id:
                      row["題目 ID"] ||
                      row["id"] ||
                      Math.random().toString(36).substring(7),
                    text:
                      row["題目"] ||
                      row["題目內容"] ||
                      row["text"] ||
                      row["question"],
                    options: options,
                    answer: ans,
                    explanation: row["解析"] || "",
                  };
                });

              if (formattedQuestions.length > 0) {
                loadedQuestions[tier] = formattedQuestions;
              }
            } catch (e) {
              console.error(`載入 ${tier} 題庫失敗:`, e);
            }
          }
        }
        setQuestionBank(loadedQuestions);
        setGameState("MENU");
      } catch (error) {
        setGameState("MENU");
      }
    };
    loadData();
  }, []);

  const applyBoastfulEffect = (handCards, prefix) => {
    const targetCards = handCards.filter(
      (c) => c.tier === "初級" || c.tier === "中級"
    );
    let pickedUids = [];

    if (prefix === "吹牛的") {
      const shuffled = [...targetCards].sort(() => Math.random() - 0.5);
      pickedUids = shuffled.slice(0, 2).map((c) => c.uid);
    } else if (prefix === "放羊小孩的") {
      pickedUids = targetCards.map((c) => c.uid);
    }

    return handCards.map((c) => {
      const baseCost = c.originalCost !== undefined ? c.originalCost : c.cost;
      if (pickedUids.includes(c.uid)) {
        return {
          ...c,
          originalCost: baseCost,
          cost: baseCost + 1,
          boasted: true,
        };
      }
      return { ...c, originalCost: baseCost, cost: baseCost, boasted: false };
    });
  };

  const startTutorial = () => {
    setPlayer({
      hp: 60,
      maxHp: 60,
      mana: 3,
      maxMana: 3,
      shield: 0,
      atkBonus: 0,
      baseShield: 0,
      drawCount: 4,
      level: 1,
      correctCount: 0,
      pendingCardReward: false,
      comboCount: 0,
      revengeActive: false,
      passives: {},
      extraClasses: [],
    });
    setPlayerClass("戰士");
    setHasVisitedSupply(false);
    setSupplyQueue([]);
    setEnemies([
      { id: "E0", name: "訓練木樁", hp: 5, maxHp: 5, atk: 0, type: "minion" },
    ]);
    setCurrentEnemyIdx(0);

    const tutorialCard = {
      id: "T1",
      uid: "tut_card_1",
      name: "基礎火球",
      tier: "初級",
      type: "攻擊",
      cost: 1,
      value: 5,
      desc: "造成 5 點傷害",
    };
    setHand([tutorialCard]);
    setDeck([]);
    setDiscardPile([]);
    setIsTutorial(true);
    setTutorialStep(1);

    setCurrentActionLogs(["【教學模式】戰鬥準備開始。"]);
    setPendingNextState("BATTLE");
    setGameState("ACTION_RESULT");
  };

  const executeNextSupplyQueue = () => {
    if (supplyQueue.length === 0) {
      setPendingNextState("");
      setGameState("BATTLE");
      return;
    }
    const nextAction = supplyQueue[0];
    setSupplyQueue(supplyQueue.slice(1));

    if (nextAction === "CARD_REWARD") {
      prepareCardReward(6);
      setCardRewardContext("battle");
      setGameState("CARD_REWARD");
      setPendingNextState("PROCESS_SUPPLY_QUEUE");
    } else if (nextAction === "CARD_REMOVAL_3") {
      setRemovalSelected([]);
      setRemovalLimit(3);
      setGameState("CARD_REMOVAL");
      setPendingNextState("PROCESS_SUPPLY_QUEUE");
    } else if (nextAction === "LEVEL_UP") {
      setLevelUpOptions(
        [...LEVEL_UP_POOL].sort(() => Math.random() - 0.5).slice(0, 3)
      );
      setGameState("LEVEL_UP");
      setPendingNextState("PROCESS_SUPPLY_QUEUE");
    }
  };

  const checkEnterSupplyZoneOrNext = () => {
    if (currentEnemyIdx === enemies.length - 1 && !hasVisitedSupply) {
      const options = SUPPLY_OPTIONS_POOL.sort(() => Math.random() - 0.5).slice(
        0,
        3
      );
      setSupplyOptions(options);
      setSelectedSupplyOptions([]);
      setGameState("SUPPLY_ZONE");
    } else {
      if (pendingNextState === "PROCESS_SUPPLY_QUEUE") {
        executeNextSupplyQueue();
      } else {
        setGameState("BATTLE");
      }
    }
  };

  const completeLevelUpFlow = (newPlayer) => {
    if (newPlayer.pendingCardReward) {
      newPlayer.pendingCardReward = false;
      setPlayer(newPlayer);
      prepareCardReward(6);
      setCardRewardContext("battle");
      setGameState("CARD_REWARD");
    } else {
      setPlayer(newPlayer);
      checkEnterSupplyZoneOrNext();
    }
  };

  const prepareCardReward = (count = 6) => {
    const highTierCards = Object.values(cardLibrary).filter(
      (c) => c.tier === "中高級" || c.tier === "高級"
    );
    const shuffledCards = highTierCards.sort(() => Math.random() - 0.5);
    setRewardCards(shuffledCards.slice(0, count));
    setSelectedRewardCards([]);
  };

  const toggleRewardCard = (card) => {
    const isSelected = selectedRewardCards.some((c) => c.id === card.id);
    const limit = 2;
    if (isSelected) {
      setSelectedRewardCards(
        selectedRewardCards.filter((c) => c.id !== card.id)
      );
    } else {
      if (selectedRewardCards.length >= limit) return;
      setSelectedRewardCards([...selectedRewardCards, card]);
    }
  };

  const confirmCardReward = () => {
    if (selectedRewardCards.length > 0) {
      let newDeck = [...deck];
      selectedRewardCards.forEach((card) => {
        newDeck.push({ ...card, uid: Math.random().toString(36).substring(7) });
      });
      setDeck(newDeck.sort(() => Math.random() - 0.5));
    }

    if (cardRewardContext === "initial") {
      setCurrentActionLogs([
        `🎁 開局神賜！你將初始強牌加入了牌庫。`,
        `遊戲開始！你選擇了【${playerClass}】，遇到了 ${enemies[0]?.name}。`,
      ]);
      setPendingNextState("BATTLE");
      setGameState("ACTION_RESULT");
    } else if (cardRewardContext === "levelup") {
      setCardRewardContext("battle");
      completeLevelUpFlow(player);
    } else {
      checkEnterSupplyZoneOrNext();
    }
  };

  const toggleRemovalCard = (cardUid) => {
    if (removalSelected.includes(cardUid)) {
      setRemovalSelected(removalSelected.filter((uid) => uid !== cardUid));
    } else {
      if (removalSelected.length >= removalLimit) return;
      setRemovalSelected([...removalSelected, cardUid]);
    }
  };

  const confirmCardRemoval = () => {
    if (removalSelected.length > 0) {
      let newHand = hand.filter((c) => !removalSelected.includes(c.uid));
      let newDeck = deck.filter((c) => !removalSelected.includes(c.uid));
      let newDiscardPile = discardPile.filter(
        (c) => !removalSelected.includes(c.uid)
      );
      setHand(newHand);
      setDeck(newDeck);
      setDiscardPile(newDiscardPile);
    }
    if (pendingNextState === "PROCESS_SUPPLY_QUEUE") {
      executeNextSupplyQueue();
    } else {
      completeLevelUpFlow(player);
    }
  };

  const openDeckBuilder = () => {
    const currentConfig = {};
    Object.values(cardLibrary).forEach((card) => {
      if (card.tier === "初級" || card.tier === "中級")
        currentConfig[card.id] = 0;
    });
    initialDeckConfig.forEach((config) => {
      if (
        cardLibrary[config.cardId] &&
        (cardLibrary[config.cardId].tier === "初級" ||
          cardLibrary[config.cardId].tier === "中級")
      ) {
        currentConfig[config.cardId] = config.count;
      }
    });
    setDeckBuilderState(currentConfig);
    setGameState("DECK_BUILDER");
  };

  const saveDeckBuilder = () => {
    const newConfig = [];
    let totalCards = 0;
    Object.entries(deckBuilderState).forEach(([cardId, count]) => {
      if (count > 0) {
        newConfig.push({ cardId, count });
        totalCards += count;
      }
    });
    if (totalCards < 5) {
      setDeckBuilderError("⚠️ 牌庫至少需要 5 張卡牌才能開始遊戲！");
      return;
    }
    setDeckBuilderError("");
    setInitialDeckConfig(newConfig);
    setGameState("MENU");
  };

  const updateCardCount = (cardId, delta) => {
    setDeckBuilderState((prev) => {
      const currentCount = prev[cardId] || 0;
      const newCount = Math.max(0, currentCount + delta);
      return { ...prev, [cardId]: newCount };
    });
  };

  const generateDeck = () => {
    const newDeck = [];
    initialDeckConfig.forEach((config) => {
      const cardTemplate = cardLibrary[config.cardId];
      if (cardTemplate) {
        for (let i = 0; i < config.count; i++) {
          newDeck.push({
            ...cardTemplate,
            uid: Math.random().toString(36).substring(7),
          });
        }
      }
    });
    return newDeck.sort(() => Math.random() - 0.5);
  };

  const submitLeaderboardScore = async () => {
    if (!playerNameInput.trim() || !user) return;
    const newEntry = {
      name: playerNameInput.trim(),
      class: playerClass,
      level: player.level,
      difficulty: difficulty,
      date: new Date().toLocaleDateString(),
      timestamp: Date.now(),
    };

    if (isCloudMode) {
      try {
        const leaderboardRef = getLeaderboardRef();
        await addDoc(leaderboardRef, newEntry);
        setHasSubmittedScore(true);
      } catch (e) {
        console.error("Error submitting score:", e);
      }
    } else {
      newEntry.id = Date.now().toString();
      const localData = JSON.parse(
        localStorage.getItem("knowledge_guardian_leaderboard") || "[]"
      );
      localData.push(newEntry);
      localStorage.setItem(
        "knowledge_guardian_leaderboard",
        JSON.stringify(localData)
      );

      const updatedData = [...localData].sort((a, b) => {
        if (b.level !== a.level) return b.level - a.level;
        return b.timestamp - a.timestamp;
      });
      setLeaderboard(updatedData);
      setHasSubmittedScore(true);
    }
  };

  const clearLeaderboard = async () => {
    if (adminPassword === "gmes") {
      if (isCloudMode) {
        try {
          for (const entry of leaderboard) {
            if (entry.id) {
              const refPath = hasMyConfig
                ? doc(db, "global_leaderboard", entry.id)
                : doc(
                    db,
                    "artifacts",
                    appId,
                    "public",
                    "data",
                    "leaderboard",
                    entry.id
                  );
              await deleteDoc(refPath);
            }
          }
        } catch (e) {
          console.error("Error clearing:", e);
        }
      } else {
        localStorage.removeItem("knowledge_guardian_leaderboard");
        setLeaderboard([]);
      }
      setShowAdminClear(false);
      setAdminPassword("");
    } else {
      setAdminPassword("密碼錯誤");
    }
  };

  const startGame = () => {
    setIsTutorial(false);
    setHasSubmittedScore(false);
    setPlayerNameInput("");
    setHasVisitedSupply(false);
    setSupplyQueue([]);
    setPendingNextState("");
    setLeaderboardTab(difficulty);

    let initialHp = playerClass === "法師" || playerClass === "盜賊" ? 45 : 60;

    setPlayer({
      hp: initialHp,
      maxHp: initialHp,
      mana: 3,
      maxMana: 3,
      shield: 0,
      atkBonus: 0,
      baseShield: 0,
      drawCount: 4,
      level: 1,
      correctCount: 0,
      pendingCardReward: false,
      comboCount: 0,
      revengeActive: false,
      passives: {},
      extraClasses: [],
    });

    const newEnemies = JSON.parse(JSON.stringify(ENEMY_LIST));
    const normalPrefixes = ["可愛的", "愛吃的", "吹牛的", "迷糊的"];
    const hardBossPrefixes = ["超級可愛", "貪吃的", "放羊小孩的", "混亂的"];

    newEnemies.forEach((enemy) => {
      if (enemy.type === "boss" && difficulty === "hard") {
        enemy.hp = 300;
        enemy.maxHp = 300;
        enemy.atk = 30;
      }

      if (difficulty === "hard" || enemy.type === "boss") {
        let prefixPool = normalPrefixes;
        if (enemy.type === "boss") {
          prefixPool =
            difficulty === "hard" ? hardBossPrefixes : normalPrefixes;
        }
        const randomPrefix =
          prefixPool[Math.floor(Math.random() * prefixPool.length)];
        enemy.name = `${randomPrefix} ${enemy.name}`;
        enemy.prefix = randomPrefix;
        enemy.eatenCount = 0;
        if (randomPrefix === "迷糊的") {
          enemy.muddledType = Math.random() < 0.5 ? "basic" : "medium";
        }
      }
    });

    setEnemies(newEnemies);
    setCurrentEnemyIdx(0);

    const shuffled = generateDeck();
    let startingHand = shuffled.slice(0, 4);
    const startingDeck = shuffled.slice(4);

    if (
      newEnemies[0].prefix === "吹牛的" ||
      newEnemies[0].prefix === "放羊小孩的"
    ) {
      startingHand = applyBoastfulEffect(startingHand, newEnemies[0].prefix);
    }

    setHand(startingHand);
    setDeck(startingDeck);
    setDiscardPile([]);

    prepareCardReward(6);
    setCardRewardContext("initial");
    setGameState("CARD_REWARD");
  };

  const toggleSupplyOption = (optId) => {
    if (selectedSupplyOptions.includes(optId)) {
      setSelectedSupplyOptions(
        selectedSupplyOptions.filter((id) => id !== optId)
      );
    } else {
      if (selectedSupplyOptions.length >= 2) return;
      setSelectedSupplyOptions([...selectedSupplyOptions, optId]);
    }
  };

  const confirmSupply = () => {
    setHasVisitedSupply(true);
    let newPlayer = { ...player };
    let logs = [
      "🏕️ 補給完成！你做出了明智的選擇。",
      "🔥 準備好迎戰最後的魔王吧！",
    ];
    let queue = [];

    selectedSupplyOptions.forEach((optId) => {
      if (optId === 1) {
        const healAmount = Math.floor(newPlayer.maxHp * 0.5);
        newPlayer.hp = Math.min(newPlayer.maxHp, newPlayer.hp + healAmount);
        logs.push(`💚 恢復生命：恢復了 ${healAmount} 點生命值。`);
      } else if (optId === 5) {
        const allClasses = ["戰士", "法師", "盜賊"];
        const currentExtra = newPlayer.extraClasses || [];
        const available = allClasses.filter(
          (c) => c !== playerClass && !currentExtra.includes(c)
        );
        if (available.length > 0) {
          const newClass =
            available[Math.floor(Math.random() * available.length)];
          newPlayer.extraClasses = [...currentExtra, newClass];
          logs.push(`🏅 附加職業：你獲得了【${newClass}】的職業天賦！`);
        }
      } else if (optId === 2) {
        queue.push("CARD_REWARD");
        logs.push(`📜 研讀秘卷：準備挑選強大的卡牌。`);
      } else if (optId === 3) {
        queue.push("CARD_REMOVAL_3");
        logs.push(`🔥 精簡牌庫：準備移除不需要的卡牌。`);
      } else if (optId === 4) {
        newPlayer.level += 1;
        queue.push("LEVEL_UP");
        logs.push(`🌟 突破極限：準備獲取升級獎勵。`);
      }
    });

    setPlayer(newPlayer);
    setCurrentActionLogs(logs);
    setSupplyQueue(queue);
    setPendingNextState("PROCESS_SUPPLY_QUEUE");
    setGameState("ACTION_RESULT");
  };

  const handleLevelUpReward = (optId) => {
    let newPlayer = { ...player };
    const option = LEVEL_UP_POOL.find((o) => o.id === optId);
    if (!option) return;

    switch (optId) {
      case 1:
        newPlayer.atkBonus += 3;
        completeLevelUpFlow(newPlayer);
        break;
      case 2:
        newPlayer.baseShield += 3;
        newPlayer.shield += 3;
        completeLevelUpFlow(newPlayer);
        break;
      case 3:
        newPlayer.maxMana += 1;
        newPlayer.mana = newPlayer.maxMana;
        completeLevelUpFlow(newPlayer);
        break;
      case 4:
        newPlayer.drawCount += 1;
        let currentDeck = [...deck];
        let currentHand = [...hand];
        let currentDiscardPile = [...discardPile];
        if (currentDeck.length === 0) {
          if (currentDiscardPile.length > 0) {
            currentDeck = currentDiscardPile.sort(() => Math.random() - 0.5);
            currentDiscardPile = [];
          } else {
            currentDeck = generateDeck();
          }
        }
        if (currentDeck.length > 0) {
          let card = currentDeck.pop();
          const currentEnemy = enemies[currentEnemyIdx];
          if (
            currentEnemy &&
            currentEnemy.prefix === "放羊小孩的" &&
            (card.tier === "初級" || card.tier === "中級")
          ) {
            const baseCost =
              card.originalCost !== undefined ? card.originalCost : card.cost;
            card = {
              ...card,
              originalCost: baseCost,
              cost: baseCost + 1,
              boasted: true,
            };
          }
          currentHand.push(card);
          setHand(currentHand);
          setDeck(currentDeck);
          setDiscardPile(currentDiscardPile);
        }
        completeLevelUpFlow(newPlayer);
        break;
      case 5:
        newPlayer.passives.comboDamage =
          (newPlayer.passives.comboDamage || 0) + 1;
        completeLevelUpFlow(newPlayer);
        break;
      case 6:
        newPlayer.passives.attackShield =
          (newPlayer.passives.attackShield || 0) + 1;
        completeLevelUpFlow(newPlayer);
        break;
      case 7:
        newPlayer.passives.revenge = (newPlayer.passives.revenge || 0) + 1;
        completeLevelUpFlow(newPlayer);
        break;
      case 8:
        setPlayer(newPlayer);
        setCardRewardContext("levelup");
        prepareCardReward(5);
        setGameState("CARD_REWARD");
        break;
      case 9:
        setPlayer(newPlayer);
        setRemovalSelected([]);
        setRemovalLimit(2);
        setGameState("CARD_REMOVAL");
        break;
    }
  };

  const playCard = (card) => {
    if (isTutorial) {
      if (tutorialStep === 1) return;
      if (tutorialStep === 2) setTutorialStep(3);
    }
    if (player.mana < card.cost) return;

    let targetTier = card.tier;
    const currentEnemy = enemies[currentEnemyIdx];

    if (currentEnemy && currentEnemy.prefix) {
      if (currentEnemy.prefix === "迷糊的") {
        if (currentEnemy.muddledType === "basic" && card.tier === "初級") {
          targetTier = "中高級";
        } else if (
          currentEnemy.muddledType === "medium" &&
          card.tier === "中級"
        ) {
          targetTier = "高級";
        }
      } else if (currentEnemy.prefix === "混亂的") {
        if (card.tier === "初級") targetTier = "中高級";
        if (card.tier === "中級") targetTier = "高級";
      }
    }

    const questionsForTier = questionBank[targetTier];
    if (!questionsForTier || questionsForTier.length === 0) return;

    setActiveCard(card);
    const randomQ =
      questionsForTier[Math.floor(Math.random() * questionsForTier.length)];
    setCurrentQuestion({ ...randomQ, targetTier });
    setGameState("QUESTION");
  };

  const handleAnswer = (selectedOption) => {
    const isCorrect = selectedOption === currentQuestion.answer;
    let newPlayer = { ...player, mana: player.mana - activeCard.cost };
    let newEnemies = [...enemies];
    let enemy = { ...newEnemies[currentEnemyIdx] };
    let nextState = "BATTLE";

    let currentHand = hand.filter((c) => c.uid !== activeCard.uid);
    let currentDeck = [...deck];
    let currentDiscardPile = [...discardPile];
    let actionLogs = [];

    if (!isTutorial || isCorrect) {
      const cleanCard = {
        ...activeCard,
        boasted: false,
        cost:
          activeCard.originalCost !== undefined
            ? activeCard.originalCost
            : activeCard.cost,
      };
      currentDiscardPile.push(cleanCard);
    }

    const drawOneCard = () => {
      if (currentDeck.length === 0) {
        if (currentDiscardPile.length > 0) {
          currentDeck = currentDiscardPile.sort(() => Math.random() - 0.5);
          currentDiscardPile = [];
          actionLogs.push("牌庫已從棄牌堆重新洗牌。");
        } else {
          currentDeck = generateDeck();
        }
      }
      if (currentDeck.length > 0) {
        let card = currentDeck.pop();
        if (
          enemy &&
          enemy.prefix === "放羊小孩的" &&
          (card.tier === "初級" || card.tier === "中級")
        ) {
          const baseCost =
            card.originalCost !== undefined ? card.originalCost : card.cost;
          card = {
            ...card,
            originalCost: baseCost,
            cost: baseCost + 1,
            boasted: true,
          };
        }
        currentHand.push(card);
        return true;
      }
      return false;
    };

    const triggerFloatingText = (text, target, color) => {
      const tid = Date.now() + Math.random();
      setFloatingTexts((prev) => [...prev, { id: tid, text, target, color }]);
      setTimeout(
        () => setFloatingTexts((prev) => prev.filter((t) => t.id !== tid)),
        1000
      );
    };

    if (isCorrect) {
      newPlayer.correctCount = (newPlayer.correctCount || 0) + 1;
      if (newPlayer.passives.comboDamage)
        newPlayer.comboCount = (newPlayer.comboCount || 0) + 1;

      actionLogs.push(`✅ 答對了！成功發動「${activeCard.name}」。`);
      if (currentQuestion.explanation)
        actionLogs.push(`💡 解析：${currentQuestion.explanation}`);

      let effectEaten = false;
      let maxEats = 0;
      if (enemy.prefix === "貪吃的") maxEats = 2;
      else if (enemy.prefix === "愛吃的") maxEats = 1;

      if (
        maxEats > 0 &&
        (activeCard.tier === "初級" || activeCard.tier === "中級")
      ) {
        if ((enemy.eatenCount || 0) < maxEats) {
          effectEaten = true;
          enemy.eatenCount = (enemy.eatenCount || 0) + 1;
          actionLogs.push(
            `😈 [${
              enemy.prefix
            }] 敵人吞掉了這張牌的效果！（本回合剩餘偷吃次數: ${
              maxEats - enemy.eatenCount
            }）`
          );
        }
      }

      if (!effectEaten) {
        let isSuperCute =
          enemy.prefix === "超級可愛" &&
          (activeCard.tier === "初級" || activeCard.tier === "中級");
        let isCute =
          enemy.prefix === "可愛的" &&
          (activeCard.tier === "初級" || activeCard.tier === "中級");

        if (hasClass("戰士")) {
          newPlayer.shield += 2;
          actionLogs.push(`🛡️ [戰士天賦] 獲得 2 點護盾！`);
        }

        switch (activeCard.type) {
          case "攻擊":
            let multiplier = 1;
            if (newPlayer.passives.comboDamage)
              multiplier +=
                (newPlayer.comboCount || 0) *
                (0.05 * newPlayer.passives.comboDamage);
            if (newPlayer.revengeActive) {
              multiplier += 0.5 * newPlayer.passives.revenge;
              newPlayer.revengeActive = false;
              actionLogs.push(
                `🔥 [復仇打擊] 怒火釋放！本次傷害提升 ${
                  newPlayer.passives.revenge * 50
                }%！`
              );
            }

            let finalAtk = Math.floor(
              (activeCard.value + (newPlayer.atkBonus || 0)) * multiplier
            );

            if (isCute || isSuperCute) {
              finalAtk = Math.floor(finalAtk / 2);
              actionLogs.push(
                `🎀 [${enemy.prefix}] 敵人太萌了！你的初/中級攻擊牌傷害減半。`
              );
            }

            enemy.hp -= finalAtk;
            actionLogs.push(`對 ${enemy.name} 造成 ${finalAtk} 點傷害。`);

            setEnemyHitState(true);
            setTimeout(() => setEnemyHitState(false), 300);
            triggerFloatingText(`-${finalAtk}`, "enemy", "text-red-500");

            if (newPlayer.passives.attackShield) {
              const shieldGain = 2 * newPlayer.passives.attackShield;
              newPlayer.shield += shieldGain;
              actionLogs.push(`🛡️ [攻擊轉盾] 獲得 ${shieldGain} 點護盾！`);
            }

            if (hasClass("法師")) {
              newPlayer.mana += 1;
              actionLogs.push(`⚡ [法師天賦] 獲得 1 點暫時法力！`);
            }
            if (hasClass("盜賊")) {
              if (drawOneCard())
                actionLogs.push(`🗡️ [盜賊天賦] 額外抽了一張牌！`);
            }
            break;

          case "防禦":
            let shieldGain = isSuperCute
              ? Math.floor(activeCard.value / 2)
              : activeCard.value;
            if (isSuperCute)
              actionLogs.push(`🎀 [超級可愛] 萌翻天！你的護盾效果減半。`);
            newPlayer.shield += shieldGain;
            actionLogs.push(`獲得了 ${shieldGain} 點護盾。`);
            triggerFloatingText(`+${shieldGain} 🛡️`, "player", "text-blue-400");
            break;

          case "法力":
            let manaGain = isSuperCute
              ? Math.floor(activeCard.value / 2)
              : activeCard.value;
            if (isSuperCute)
              actionLogs.push(`🎀 [超級可愛] 萌翻天！你的法力恢復減半。`);
            newPlayer.mana += manaGain;
            actionLogs.push(`獲得了 ${manaGain} 點暫時法力。`);
            triggerFloatingText(`+${manaGain} ⚡`, "player", "text-cyan-400");
            break;

          case "治療":
            let healGain = isSuperCute
              ? Math.floor(activeCard.value / 2)
              : activeCard.value;
            if (isSuperCute)
              actionLogs.push(`🎀 [超級可愛] 萌翻天！你的治療效果減半。`);
            newPlayer.hp = Math.min(newPlayer.maxHp, newPlayer.hp + healGain);
            actionLogs.push(`恢復了 ${healGain} 點生命值。`);
            triggerFloatingText(`+${healGain} 💚`, "player", "text-green-400");
            break;

          case "特殊":
            let drawGain = isSuperCute
              ? Math.floor(activeCard.value / 2)
              : activeCard.value;
            if (isSuperCute)
              actionLogs.push(`🎀 [超級可愛] 萌翻天！你的抽牌數量減半。`);
            let drawnCards = 0;
            for (let i = 0; i < drawGain; i++) {
              if (drawOneCard()) drawnCards++;
            }
            actionLogs.push(`✨ 發動特殊效果，抽了 ${drawnCards} 張牌！`);
            if (drawnCards > 0)
              triggerFloatingText(
                `抽牌 x${drawnCards}`,
                "player",
                "text-indigo-400"
              );
            break;
        }
      }
    } else {
      actionLogs.push(
        `❌ 答錯了！正確答案是「${currentQuestion.answer}」，卡牌失效。`
      );
      if (currentQuestion.explanation)
        actionLogs.push(`💡 解析：${currentQuestion.explanation}`);

      if (newPlayer.passives.comboDamage) {
        newPlayer.comboCount = 0;
        actionLogs.push(`📉 連續答對中斷！增傷已重置。`);
      }
      if (activeCard.type === "攻擊" && newPlayer.passives.revenge) {
        newPlayer.revengeActive = true;
        actionLogs.push(`💀 [復仇打擊] 準備就緒！下一次攻擊將獲得額外傷害。`);
      }
    }

    let isEnemyDead = false;
    if (enemy.hp <= 0) {
      enemy.hp = 0;
      isEnemyDead = true;
      if (!isTutorial) {
        if (currentEnemyIdx === enemies.length - 1) {
          nextState = "VICTORY";
        } else {
          const nextIdx = currentEnemyIdx + 1;
          setCurrentEnemyIdx(nextIdx);
          actionLogs.push(`🏆 擊敗了 ${enemy.name}！下一位敵人出現了。`);

          newPlayer.mana = newPlayer.maxMana;
          newPlayer.shield = newPlayer.baseShield || 0;
          newPlayer.comboCount = 0;
          newPlayer.revengeActive = false;

          let allCards = [
            ...currentDeck,
            ...currentHand,
            ...currentDiscardPile,
          ].map((c) => ({
            ...c,
            boasted: false,
            cost: c.originalCost !== undefined ? c.originalCost : c.cost,
          }));
          allCards.sort(() => Math.random() - 0.5);

          const drawLimit = newPlayer.drawCount || 4;
          currentHand = allCards.slice(0, drawLimit);
          currentDeck = allCards.slice(drawLimit);
          currentDiscardPile = [];

          if (
            newEnemies[nextIdx].prefix === "吹牛的" ||
            newEnemies[nextIdx].prefix === "放羊小孩的"
          ) {
            currentHand = applyBoastfulEffect(
              currentHand,
              newEnemies[nextIdx].prefix
            );
          }
        }
      }
    }

    newEnemies[currentEnemyIdx] = enemy;

    let isLevelUp = false;
    if (
      nextState !== "VICTORY" &&
      !isTutorial &&
      isCorrect &&
      newPlayer.correctCount > 0 &&
      newPlayer.correctCount % 10 === 0
    ) {
      isLevelUp = true;
      newPlayer.level += 1;
    }

    if (isTutorial) {
      if (isCorrect) {
        setTutorialStep(4);
        nextState = "BATTLE";
      } else {
        setTutorialStep(2);
        newPlayer.mana += activeCard.cost;
        currentHand.push(activeCard);
        actionLogs.push(
          `導師：哎呀答錯了！沒關係，練習模式下法力跟卡牌已恢復。再試一次！`
        );
        nextState = "BATTLE";
      }
    } else {
      if (nextState === "VICTORY") {
      } else if (isLevelUp) {
        nextState = "LEVEL_UP";
        setLevelUpOptions(
          [...LEVEL_UP_POOL].sort(() => Math.random() - 0.5).slice(0, 3)
        );
        if (isEnemyDead) newPlayer.pendingCardReward = true;
      } else if (isEnemyDead) {
        nextState = "CARD_REWARD";
        setCardRewardContext("battle");
        prepareCardReward(6);
      }
    }

    setHand(currentHand);
    setDeck(currentDeck);
    setDiscardPile(currentDiscardPile);
    setPlayer(newPlayer);
    setEnemies(newEnemies);
    setActiveCard(null);
    setCurrentQuestion(null);

    setCurrentActionLogs(actionLogs);
    setPendingNextState(nextState);
    setGameState("ACTION_RESULT");
  };

  const endTurn = () => {
    if (isTutorial) return;

    let newEnemies = [...enemies];
    let enemy = { ...newEnemies[currentEnemyIdx] };

    let currentShield = player.shield;
    let currentHp = player.hp;
    let damageToPlayer = enemy.atk;
    let actionLogs = [];

    if (currentShield >= damageToPlayer) {
      currentShield -= damageToPlayer;
      damageToPlayer = 0;
    } else {
      damageToPlayer -= currentShield;
      currentShield = 0;
      currentHp -= damageToPlayer;
    }

    actionLogs.push(
      `⚠️ 回合結束。${enemy.name} 攻擊！造成 ${enemy.atk} 點傷害 (實際扣除 ${damageToPlayer} 點生命)。`
    );

    if (damageToPlayer > 0) {
      setPlayerHitState(true);
      setTimeout(() => setPlayerHitState(false), 300);
      const tid = Date.now() + Math.random();
      setFloatingTexts((prev) => [
        ...prev,
        {
          id: tid,
          text: `-${damageToPlayer}`,
          target: "player",
          color: "text-red-500",
        },
      ]);
      setTimeout(
        () => setFloatingTexts((prev) => prev.filter((t) => t.id !== tid)),
        1000
      );
    } else if (enemy.atk > 0) {
      const tid = Date.now() + Math.random();
      setFloatingTexts((prev) => [
        ...prev,
        { id: tid, text: `擋下!`, target: "player", color: "text-blue-400" },
      ]);
      setTimeout(
        () => setFloatingTexts((prev) => prev.filter((t) => t.id !== tid)),
        1000
      );
    }

    setPlayer({
      ...player,
      hp: currentHp,
      shield: hasClass("戰士")
        ? currentShield + (player.baseShield || 0)
        : player.baseShield || 0,
      mana: player.maxMana,
    });

    let currentHand = [...hand];
    let currentDeck = [...deck];
    let currentDiscardPile = [...discardPile];
    const drawLimit = player.drawCount || 4;

    if (currentHand.length > 0) {
      const cleanHand = currentHand.map((c) => ({
        ...c,
        boasted: false,
        cost: c.originalCost !== undefined ? c.originalCost : c.cost,
      }));
      currentDiscardPile.push(...cleanHand);
      currentHand = [];
    }

    while (currentHand.length < drawLimit) {
      if (currentDeck.length === 0) {
        if (currentDiscardPile.length > 0) {
          actionLogs.push("牌庫已從棄牌堆重新洗牌。");
          currentDeck = currentDiscardPile.sort(() => Math.random() - 0.5);
          currentDiscardPile = [];
        } else {
          currentDeck = generateDeck();
        }
      }
      if (currentDeck.length > 0) {
        currentHand.push(currentDeck.pop());
      } else {
        break;
      }
    }

    if (enemy.prefix) {
      enemy.eatenCount = 0;
      if (enemy.prefix === "吹牛的" || enemy.prefix === "放羊小孩的") {
        currentHand = applyBoastfulEffect(currentHand, enemy.prefix);
      }
    }

    newEnemies[currentEnemyIdx] = enemy;

    setHand(currentHand);
    setDeck(currentDeck);
    setDiscardPile(currentDiscardPile);
    setEnemies(newEnemies);

    setCurrentActionLogs(actionLogs);
    setPendingNextState(currentHp <= 0 ? "GAMEOVER" : "BATTLE");
    setGameState("ACTION_RESULT");
  };

  const proceedFromActionResult = () => {
    if (pendingNextState === "PROCESS_SUPPLY_QUEUE") {
      executeNextSupplyQueue();
    } else {
      setGameState(pendingNextState);
    }
  };

  const renderCard = (
    card,
    onClick = null,
    disabled = false,
    isSelected = false,
    isRemoval = false
  ) => {
    const typeColors = {
      攻擊: "bg-red-900 border-red-500 text-red-100",
      防禦: "bg-blue-900 border-blue-500 text-blue-100",
      法力: "bg-purple-900 border-purple-500 text-purple-100",
      治療: "bg-green-900 border-green-500 text-green-100",
      特殊: "bg-indigo-900 border-indigo-500 text-indigo-100",
    };

    let TypeIcon = HelpCircle;
    if (card.type === "攻擊") TypeIcon = Swords;
    else if (card.type === "防禦") TypeIcon = Shield;
    else if (card.type === "法力") TypeIcon = Zap;
    else if (card.type === "治療") TypeIcon = Heart;
    else if (card.type === "特殊") TypeIcon = RefreshCcw;

    let displayDesc = card.desc;
    if (card.type === "攻擊" && (player.atkBonus || 0) > 0) {
      displayDesc = (
        <span>
          {card.desc}{" "}
          <span className="text-green-400 font-bold">(+{player.atkBonus})</span>
        </span>
      );
    }

    return (
      <div
        key={card.uid || card.id}
        onClick={() => !disabled && onClick && onClick(card)}
        className={`relative w-40 h-56 rounded-xl border-2 p-3 flex flex-col justify-between cursor-pointer transition-transform shadow-lg ${
          typeColors[card.type] ||
          "bg-slate-900 border-slate-500 text-slate-100"
        } ${
          disabled
            ? "opacity-50 cursor-not-allowed hover:translate-y-0"
            : "hover:-translate-y-2"
        } ${
          isSelected
            ? isRemoval
              ? "ring-4 ring-red-500 shadow-[0_0_20px_rgba(239,68,68,0.6)] -translate-y-2 opacity-60"
              : "ring-4 ring-green-500 shadow-[0_0_20px_rgba(34,197,94,0.6)] -translate-y-2"
            : ""
        }`}
      >
        <div className="flex justify-between items-start">
          <div
            className={`${
              card.boasted
                ? "bg-red-600 border-red-300 animate-pulse shadow-[0_0_15px_rgba(220,38,38,0.8)]"
                : "bg-blue-500 border-blue-300"
            } text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg border-2 shadow-md transition-colors`}
          >
            {card.cost}
          </div>
          <span className="text-xs bg-black/50 px-2 py-1 rounded-full">
            {card.tier}
          </span>
        </div>
        <div className="text-center flex flex-col items-center">
          <TypeIcon size={32} className="mb-2 opacity-80" />
          <h3 className="font-bold text-lg leading-tight">{card.name}</h3>
        </div>
        <div className="text-xs text-center bg-black/30 p-2 rounded">
          {displayDesc}
        </div>
      </div>
    );
  };

  const displayedLeaderboard = leaderboard.filter(
    (entry) => (entry.difficulty || "normal") === leaderboardTab
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans p-4 flex flex-col">
      <style>{`
        @keyframes shake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-10px) rotate(-5deg); } 40% { transform: translateX(10px) rotate(5deg); } 60% { transform: translateX(-10px) rotate(-5deg); } 80% { transform: translateX(10px) rotate(5deg); } }
        .animate-shake { animation: shake 0.3s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes breathe { 0%, 100% { transform: scale(1) translateY(0); } 50% { transform: scale(1.05) translateY(-5px); } }
        .animate-breathe { animation: breathe 3s infinite ease-in-out; }
        @keyframes float-damage { 0% { opacity: 1; transform: translateY(0) scale(1); } 100% { opacity: 0; transform: translateY(-60px) scale(1.5); } }
        .animate-float-damage { animation: float-damage 1s ease-out forwards; }
      `}</style>

      <header className="flex justify-between items-center py-2 px-4 bg-slate-900 rounded-lg shadow-md mb-4 border border-slate-800">
        <h1 className="text-xl font-bold flex items-center gap-2 text-yellow-400">
          <HelpCircle size={24} /> 知識守護者
        </h1>
        {["BATTLE", "ACTION_RESULT"].includes(gameState) && !isTutorial && (
          <div className="flex gap-4 items-center">
            <span
              className={`text-xs px-3 py-1 rounded-full font-bold border ${
                difficulty === "hard"
                  ? "bg-red-900/30 text-red-400 border-red-600/50"
                  : "bg-blue-900/30 text-blue-400 border-blue-600/50"
              }`}
            >
              {difficulty === "hard" ? "困難模式" : "一般模式"}
            </span>
            <button
              onClick={startTutorial}
              className="text-slate-400 hover:text-white flex items-center gap-1 text-sm bg-slate-800 hover:bg-slate-700 px-3 py-1 rounded-full transition-colors"
            >
              <BookOpen size={16} /> 重新教學
            </button>
            <span className="text-slate-400">
              關卡: {currentEnemyIdx + 1} / 5
            </span>
          </div>
        )}
        {["BATTLE", "ACTION_RESULT"].includes(gameState) && isTutorial && (
          <div className="flex gap-4 items-center">
            <span className="text-yellow-500 font-bold bg-yellow-900/30 px-3 py-1 rounded-full">
              新手教學中
            </span>
            <button
              onClick={() => {
                setIsTutorial(false);
                setGameState("MENU");
              }}
              className="text-slate-400 hover:text-white flex items-center gap-1 text-sm bg-slate-800 hover:bg-red-900 px-3 py-1 rounded-full transition-colors"
            >
              <X size={16} /> 退出教學
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 flex flex-col gap-4 relative max-w-5xl mx-auto w-full">
        {isTutorial && tutorialStep > 0 && (
          <div
            className={`absolute left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4 pointer-events-none transition-all duration-300 ${
              ["QUESTION", "ACTION_RESULT"].includes(gameState)
                ? "top-2 opacity-50 scale-90"
                : "top-8 opacity-100 scale-100"
            }`}
          >
            <div className="bg-slate-800 border-2 border-yellow-500 rounded-2xl p-4 shadow-[0_0_30px_rgba(234,179,8,0.3)] flex items-start gap-4 pointer-events-auto">
              <div className="text-6xl drop-shadow-md animate-bounce">🧙‍♂️</div>
              <div className="flex-1">
                <h3 className="text-yellow-400 font-bold mb-1 text-lg">
                  守護者導師
                </h3>
                <p className="text-white leading-relaxed">
                  {tutorialStep === 1 &&
                    "歡迎來到知識守護者！我是你的導師。接下來讓我教你如何戰鬥，準備好擊碎前面的『訓練木樁』了嗎？"}
                  {tutorialStep === 2 &&
                    "畫面下方是你的【手牌】。打出卡牌需要消耗紫色的【法力值】。現在，點擊手牌中的「基礎火球」吧！"}
                  {tutorialStep === 3 &&
                    "魔法不是隨便就能發動的，你必須證明自己的智慧！選出正確的答案，才能成功施放火球！"}
                  {tutorialStep === 4 &&
                    "太完美了！木樁被擊碎了！答對問題才能發動卡牌，【戰士】還能獲得額外護盾喔！準備好迎接真正的挑戰了嗎？"}
                </p>
                <div className="mt-4 flex justify-end">
                  {tutorialStep === 1 && (
                    <button
                      onClick={() => setTutorialStep(2)}
                      className="bg-blue-600 hover:bg-blue-500 px-6 py-2 rounded-lg font-bold text-sm shadow-md transition-transform hover:scale-105"
                    >
                      準備好了！
                    </button>
                  )}
                  {tutorialStep === 4 && (
                    <button
                      onClick={() => {
                        setIsTutorial(false);
                        setGameState("MENU");
                      }}
                      className="bg-green-600 hover:bg-green-500 px-6 py-2 rounded-lg font-bold text-sm shadow-md transition-transform hover:scale-105"
                    >
                      結束教學，回到主選單
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {gameState === "LOADING" && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-950 z-50">
            <div className="text-xl text-yellow-400 animate-pulse">
              正在載入遊戲資料...
            </div>
          </div>
        )}

        {gameState === "MENU" && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-950 z-50 p-4">
            <div className="text-center bg-slate-900 p-8 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full relative">
              <button
                onClick={() => setGameState("LEADERBOARD")}
                className="absolute top-4 right-4 text-yellow-500 bg-yellow-900/30 hover:bg-yellow-500 hover:text-white p-2 rounded-full transition-colors flex items-center gap-1 text-sm font-bold border border-yellow-600/50"
              >
                <Trophy size={16} /> 榮譽榜
              </button>
              <Swords size={64} className="mx-auto mb-4 text-yellow-500 mt-4" />
              <h1 className="text-4xl font-extrabold mb-2 text-white">
                知識守護者
              </h1>
              <p className="text-slate-400 mb-6 leading-relaxed text-sm">
                結合卡牌對戰與答題的智慧考驗！
                <br />
                用你的知識擊敗 4 隻小兵與最終魔王。
              </p>

              <div className="mb-6">
                <h3 className="text-slate-300 font-bold mb-3 text-left">
                  選擇職業：
                </h3>
                <div className="flex flex-col gap-2">
                  {[
                    {
                      id: "戰士",
                      icon: Shield,
                      desc: "答對問題時獲得2點護盾，且跨回合不會歸零",
                      color: "text-blue-400",
                      bg: "bg-blue-900/30",
                      border: "border-blue-500",
                    },
                    {
                      id: "法師",
                      icon: Zap,
                      desc: "造成傷害時獲得一點法力",
                      color: "text-purple-400",
                      bg: "bg-purple-900/30",
                      border: "border-purple-500",
                    },
                    {
                      id: "盜賊",
                      icon: Swords,
                      desc: "造成傷害時額外抽一張卡片",
                      color: "text-red-400",
                      bg: "bg-red-900/30",
                      border: "border-red-500",
                    },
                  ].map((cls) => (
                    <div
                      key={cls.id}
                      onClick={() => setPlayerClass(cls.id)}
                      className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        playerClass === cls.id
                          ? `${cls.bg} ${cls.border}`
                          : "border-slate-700 hover:border-slate-500 bg-slate-800"
                      }`}
                    >
                      <div className={`mr-4 ${cls.color}`}>
                        <cls.icon size={24} />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">{cls.id}</div>
                        <div className="text-xs text-slate-400">{cls.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-slate-300 font-bold mb-3 text-left">
                  選擇難度：
                </h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => setDifficulty("normal")}
                    className={`flex-1 py-3 rounded-xl border-2 font-bold transition-all shadow-md ${
                      difficulty === "normal"
                        ? "bg-blue-600 border-blue-400 text-white"
                        : "bg-slate-800 border-slate-600 text-slate-400"
                    }`}
                  >
                    一般模式
                  </button>
                  <button
                    onClick={() => setDifficulty("hard")}
                    className={`flex-1 py-3 rounded-xl border-2 font-bold transition-all shadow-md ${
                      difficulty === "hard"
                        ? "bg-red-600 border-red-400 text-white shadow-[0_0_15px_rgba(220,38,38,0.4)]"
                        : "bg-slate-800 border-slate-600 text-slate-400"
                    }`}
                  >
                    困難模式
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <button
                  onClick={startGame}
                  className="bg-yellow-600 hover:bg-yellow-500 text-white font-bold py-3 px-8 rounded-full text-lg transition-all transform hover:scale-105 shadow-[0_0_15px_rgba(202,138,4,0.5)]"
                >
                  開始冒險
                </button>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={startTutorial}
                    className="flex-1 bg-blue-700 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-full text-md transition-all flex justify-center items-center gap-2"
                  >
                    <BookOpen size={20} /> 實戰教學
                  </button>
                  <button
                    onClick={openDeckBuilder}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-4 rounded-full text-md transition-all flex justify-center items-center gap-2"
                  >
                    <Settings size={20} /> 自訂牌庫
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {gameState === "LEADERBOARD" && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-950 z-50 p-4">
            <div className="bg-slate-900 p-8 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full relative">
              <button
                onClick={() => setGameState("MENU")}
                className="absolute top-4 right-4 text-slate-400 hover:text-white bg-slate-800 hover:bg-red-900 p-2 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
              <div className="text-center mb-6">
                <Trophy size={48} className="mx-auto mb-2 text-yellow-500" />
                <h2 className="text-3xl font-bold text-yellow-400">
                  英雄榮譽榜
                </h2>
                {isCloudMode ? (
                  <div className="text-xs text-green-400 mt-2">
                    ● 全球同步模式
                  </div>
                ) : (
                  <div className="text-xs text-orange-400 mt-2">
                    ● 區域單機模式 (請設定 Firebase)
                  </div>
                )}
                {cloudError && (
                  <div className="text-xs text-red-400 mt-1">{cloudError}</div>
                )}
              </div>

              <div className="flex mb-4 gap-2">
                <button
                  onClick={() => setLeaderboardTab("normal")}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg border border-slate-700 transition-colors ${
                    leaderboardTab === "normal"
                      ? "bg-blue-600 text-white border-blue-400"
                      : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                  }`}
                >
                  一般模式
                </button>
                <button
                  onClick={() => setLeaderboardTab("hard")}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg border border-slate-700 transition-colors ${
                    leaderboardTab === "hard"
                      ? "bg-red-600 text-white border-red-400"
                      : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                  }`}
                >
                  困難模式
                </button>
              </div>

              <div className="bg-slate-950 rounded-xl border border-slate-800 p-4 min-h-[250px] max-h-[50vh] overflow-y-auto custom-scrollbar">
                {displayedLeaderboard.length === 0 ? (
                  <div className="text-center text-slate-500 py-10">
                    尚無英雄留名...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {displayedLeaderboard.map((entry, idx) => (
                      <div
                        key={idx}
                        className={`flex justify-between items-center bg-slate-900 px-4 py-3 rounded-lg text-sm border shadow-sm ${
                          entry.difficulty === "hard"
                            ? "border-red-900/50"
                            : "border-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-yellow-600 font-bold w-4">
                            {idx + 1}.
                          </span>
                          <span className="text-white font-bold text-base">
                            {entry.name}
                          </span>
                          <span className="text-slate-400 text-xs bg-slate-800 px-2 py-0.5 rounded">
                            {entry.class} Lv.{entry.level}
                          </span>
                        </div>
                        <span className="text-slate-500 text-xs">
                          {entry.date}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-between items-center">
                <div className="w-full text-right">
                  {showAdminClear ? (
                    <div className="flex items-center justify-end gap-2">
                      <input
                        type="password"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="輸入密碼"
                        className={`w-20 px-2 py-1 text-xs bg-slate-800 border rounded text-white ${
                          adminPassword === "密碼錯誤"
                            ? "border-red-500 text-red-400"
                            : "border-slate-600"
                        }`}
                        onFocus={() =>
                          adminPassword === "密碼錯誤" && setAdminPassword("")
                        }
                      />
                      <button
                        onClick={clearLeaderboard}
                        className="text-xs bg-red-900 text-white px-2 py-1 rounded hover:bg-red-800"
                      >
                        確認
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminClear(false);
                          setAdminPassword("");
                        }}
                        className="text-xs bg-slate-700 text-white px-2 py-1 rounded"
                      >
                        取消
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAdminClear(true)}
                      className="text-xs text-slate-500 hover:text-red-400 transition-colors bg-slate-900 px-2 py-1 rounded"
                    >
                      清空榮譽榜
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {gameState === "DECK_BUILDER" && (
          <div className="absolute inset-0 flex flex-col bg-slate-950 z-50 p-6 overflow-hidden">
            <div className="flex justify-between items-center mb-6 bg-slate-900 p-4 rounded-xl border border-slate-700 shadow-md">
              <h2 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
                <Settings size={24} /> 編輯初始牌庫
              </h2>
              <div className="flex items-center gap-6">
                {deckBuilderError && (
                  <span className="text-red-400 font-bold text-sm bg-red-900/30 px-3 py-1 rounded">
                    {deckBuilderError}
                  </span>
                )}
                <span className="text-slate-300 font-bold bg-slate-800 px-4 py-2 rounded-lg">
                  總卡牌數:{" "}
                  <span className="text-blue-400 text-xl">
                    {Object.values(deckBuilderState).reduce((a, b) => a + b, 0)}
                  </span>
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={() => setGameState("MENU")}
                    className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={saveDeckBuilder}
                    className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded-lg font-bold shadow-[0_0_10px_rgba(22,163,74,0.4)] transition-colors"
                  >
                    儲存配置
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 pb-20">
                {Object.values(cardLibrary)
                  .filter(
                    (card) => card.tier === "初級" || card.tier === "中級"
                  )
                  .map((card) => (
                    <div
                      key={card.id}
                      className="flex flex-col items-center gap-3 bg-slate-900 p-4 rounded-2xl border border-slate-800 hover:border-slate-600 transition-colors"
                    >
                      <div className="transform scale-90 origin-top">
                        {renderCard(card, null, false)}
                      </div>
                      <div className="flex items-center gap-4 bg-slate-950 p-2 rounded-xl w-full justify-center border border-slate-800">
                        <button
                          onClick={() => updateCardCount(card.id, -1)}
                          disabled={(deckBuilderState[card.id] || 0) <= 0}
                          className="w-10 h-10 bg-red-900/80 hover:bg-red-700 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-lg font-bold text-xl flex items-center justify-center transition-colors"
                        >
                          -
                        </button>
                        <span className="font-bold w-6 text-center text-xl text-yellow-400">
                          {deckBuilderState[card.id] || 0}
                        </span>
                        <button
                          onClick={() => updateCardCount(card.id, 1)}
                          className="w-10 h-10 bg-blue-900/80 hover:bg-blue-700 text-white rounded-lg font-bold text-xl flex items-center justify-center transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {(gameState === "GAMEOVER" || gameState === "VICTORY") && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-50 backdrop-blur-sm p-4">
            <div className="bg-slate-900 p-8 rounded-2xl border border-slate-700 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar flex flex-col">
              <div className="text-center mb-6 shrink-0">
                {gameState === "VICTORY" ? (
                  <>
                    <div className="text-6xl mb-4">🏆</div>
                    <h1 className="text-4xl font-bold text-yellow-400 mb-4">
                      挑戰成功！
                    </h1>
                    <p className="mb-2 text-slate-300">
                      你用無窮的知識打敗了遠古惡龍！
                    </p>
                  </>
                ) : (
                  <>
                    <Skull size={64} className="mx-auto mb-4 text-red-500" />
                    <h1 className="text-4xl font-bold text-red-400 mb-4">
                      挑戰失敗
                    </h1>
                    <p className="mb-2 text-slate-300">
                      你的生命值歸零了，知識需要再磨練！
                    </p>
                  </>
                )}
              </div>

              {gameState === "VICTORY" && !hasSubmittedScore && (
                <div className="mb-6 bg-slate-800 p-6 rounded-xl border border-yellow-600/50 shadow-inner shrink-0">
                  <h3 className="text-xl font-bold text-white mb-4 flex justify-center items-center gap-2">
                    登錄英雄榮譽榜 (
                    {difficulty === "hard" ? "困難模式" : "一般模式"})
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <input
                      type="text"
                      value={playerNameInput}
                      onChange={(e) => setPlayerNameInput(e.target.value)}
                      placeholder="輸入你的名字或代號..."
                      className="px-4 py-3 rounded-lg bg-slate-950 border border-slate-600 text-white focus:outline-none focus:border-yellow-500 w-full sm:w-64"
                      maxLength={15}
                    />
                    <button
                      onClick={submitLeaderboardScore}
                      className="bg-yellow-600 hover:bg-yellow-500 text-white font-bold py-3 px-8 rounded-lg transition-colors whitespace-nowrap shadow-md"
                    >
                      名留青史
                    </button>
                  </div>
                </div>
              )}
              {gameState === "VICTORY" && hasSubmittedScore && (
                <div className="mb-6 text-green-400 font-bold bg-green-900/20 py-4 rounded-xl border border-green-800/50 text-center shrink-0">
                  🎉 已成功將你的光榮事蹟刻在榮譽榜上！
                </div>
              )}

              <div className="mb-6 bg-slate-950 rounded-xl border border-slate-800 p-4 flex-1 flex flex-col min-h-[250px]">
                <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-2 px-2 shrink-0">
                  <h3 className="text-lg font-bold text-yellow-500 flex items-center gap-2">
                    <Trophy size={18} /> 英雄名冊 (
                    {difficulty === "hard" ? "困難模式" : "一般模式"})
                    {isCloudMode ? (
                      <span className="text-xs text-green-400 ml-2">
                        (全球同步)
                      </span>
                    ) : (
                      <span className="text-xs text-orange-400 ml-2">
                        (單機版)
                      </span>
                    )}
                  </h3>
                  {showAdminClear ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="password"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="輸入密碼"
                        className={`w-20 px-2 py-1 text-xs bg-slate-800 border rounded text-white ${
                          adminPassword === "密碼錯誤"
                            ? "border-red-500 text-red-400"
                            : "border-slate-600"
                        }`}
                        onFocus={() =>
                          adminPassword === "密碼錯誤" && setAdminPassword("")
                        }
                      />
                      <button
                        onClick={clearLeaderboard}
                        className="text-xs bg-red-900 text-white px-2 py-1 rounded hover:bg-red-800"
                      >
                        確認
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminClear(false);
                          setAdminPassword("");
                        }}
                        className="text-xs bg-slate-700 text-white px-2 py-1 rounded"
                      >
                        取消
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAdminClear(true)}
                      className="text-xs text-slate-500 hover:text-red-400 transition-colors bg-slate-900 px-2 py-1 rounded"
                    >
                      清空榮譽榜
                    </button>
                  )}
                </div>
                {cloudError && (
                  <div className="text-xs text-red-400 mb-2 px-2">
                    {cloudError}
                  </div>
                )}
                <div className="space-y-2 overflow-y-auto custom-scrollbar text-left px-2 flex-1">
                  {displayedLeaderboard.length === 0 ? (
                    <div className="text-center text-slate-500 py-4">
                      尚無英雄留名...
                    </div>
                  ) : (
                    displayedLeaderboard.map((entry, idx) => (
                      <div
                        key={entry.id || idx}
                        className={`flex justify-between items-center bg-slate-900 px-4 py-2 rounded-lg text-sm border shrink-0 ${
                          entry.difficulty === "hard"
                            ? "border-red-900/50"
                            : "border-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-yellow-600 font-bold w-4">
                            {idx + 1}.
                          </span>
                          <span className="text-white font-bold">
                            {entry.name}
                          </span>
                          <span className="text-slate-400 text-xs bg-slate-800 px-2 py-0.5 rounded">
                            {entry.class} Lv.{entry.level}
                          </span>
                        </div>
                        <span className="text-slate-500 text-xs">
                          {entry.date}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <button
                onClick={() => setGameState("MENU")}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-full flex items-center mx-auto gap-2 shrink-0"
              >
                <RefreshCcw size={20} /> 返回主選單
              </button>
            </div>
          </div>
        )}

        {gameState === "LEVEL_UP" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50 backdrop-blur-md">
            <div className="bg-slate-800 p-8 rounded-2xl border-2 border-yellow-500 shadow-[0_0_30px_rgba(234,179,8,0.5)] max-w-4xl w-full mx-4 text-center transform transition-all">
              <div className="text-6xl mb-4 animate-bounce">🌟</div>
              <h2 className="text-3xl font-bold mb-2 text-yellow-400 tracking-wider">
                等級提升！
              </h2>
              <p className="text-slate-300 mb-8">
                你已經累積答對了 {player.correctCount}{" "}
                題，請從下列隨機抽出的秘寶中選擇一項：
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {levelUpOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => handleLevelUpReward(opt.id)}
                    className={`bg-slate-900 border-2 border-slate-600 ${opt.border} text-white font-semibold p-6 rounded-2xl flex flex-col items-center gap-4 transition-all hover:-translate-y-2 shadow-lg group`}
                  >
                    <div
                      className={`${opt.bg} p-4 rounded-full ${opt.color} transition-colors`}
                    >
                      <opt.icon size={36} />
                    </div>
                    <div>
                      <div
                        className={`text-xl mb-2 font-bold ${opt.color} group-hover:text-white transition-colors`}
                      >
                        {opt.title}
                      </div>
                      <div className="text-sm text-slate-400 leading-relaxed">
                        {opt.desc}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* --- 升級：戰前補給營地 (隨機 3 選 2) --- */}
        {gameState === "SUPPLY_ZONE" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50 backdrop-blur-md overflow-y-auto">
            <div className="bg-slate-900 p-8 rounded-2xl border-2 border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.5)] max-w-5xl w-full mx-4 text-center my-8">
              <div className="text-6xl mb-4">🏕️</div>
              <h2 className="text-3xl font-bold mb-6 text-green-400 tracking-wider">
                戰前補給營地
              </h2>

              <div className="bg-red-950/50 border border-red-500 p-6 rounded-xl mb-8 inline-block shadow-inner text-left">
                <h3 className="text-xl font-bold text-red-400 mb-2 flex items-center gap-2">
                  <Skull size={24} /> 魔王情報警告{" "}
                  {difficulty === "hard" ? "(困難模式)" : ""}
                </h3>
                <p className="text-slate-200 text-lg leading-relaxed">
                  即將面對的遠古惡龍散發著令人窒息的壓迫感...
                  <br />
                  據傳，魔王對{" "}
                  <span className="text-yellow-400 font-bold">
                    「初級」與「中級」
                  </span>{" "}
                  卡牌具有強大的抗性，可能會使這些卡牌傷害減半、吞噬卡牌效果、增加發動的耗魔，甚至竄改題目的難度！
                  <br />
                  {difficulty === "hard" && (
                    <span className="text-red-400 font-bold block mt-2">
                      🔥
                      警告：在困難模式中，魔王的技能將更加致命，你的初/中級卡牌效果將「全部減半」、「耗魔大幅增加」或面臨「地獄級難度題庫」！
                    </span>
                  )}
                  <span className="text-green-400 mt-2 block">
                    💡
                    建議策略：盡可能保留法力，依賴「中高級」與「高級」卡牌作為主力輸出。
                  </span>
                </p>
              </div>

              <p className="text-slate-300 mb-6">
                決戰即將到來，請從下方隨機抽出的選項中，選擇{" "}
                <span className="text-yellow-400 font-bold text-xl">2 項</span>{" "}
                最後的補給 ({selectedSupplyOptions.length}/2)：
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {supplyOptions.map((opt) => {
                  const isSelected = selectedSupplyOptions.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleSupplyOption(opt.id)}
                      className={`bg-slate-800 border-2 ${
                        isSelected
                          ? "border-green-400 ring-4 ring-green-500/50 scale-105"
                          : opt.border
                      } hover:bg-slate-700 text-white font-semibold p-6 rounded-2xl flex flex-col items-center gap-4 transition-all shadow-lg relative`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-md">
                          ✓
                        </div>
                      )}
                      <div
                        className={`${opt.bg} p-4 rounded-full ${opt.color} transition-colors`}
                      >
                        <opt.icon size={36} />
                      </div>
                      <div>
                        <div className={`text-xl mb-2 font-bold ${opt.color}`}>
                          {opt.title}
                        </div>
                        <div className="text-sm text-slate-400 leading-relaxed">
                          {opt.desc}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={confirmSupply}
                  disabled={selectedSupplyOptions.length !== 2}
                  className={`px-10 py-4 rounded-xl font-bold text-xl transition-all shadow-lg flex items-center gap-2 ${
                    selectedSupplyOptions.length === 2
                      ? "bg-green-600 hover:bg-green-500 text-white"
                      : "bg-slate-700 text-slate-500 cursor-not-allowed"
                  }`}
                >
                  確認補給並迎戰魔王
                </button>
              </div>
            </div>
          </div>
        )}

        {gameState === "CARD_REWARD" &&
          (() => {
            const rewardLimit = 2; // 全部情境都設定為 2 張
            let title = "⚔️ 戰鬥勝利：戰利品選擇";
            if (cardRewardContext === "levelup")
              title = "🌟 升級秘寶：進階卡牌";
            if (cardRewardContext === "initial")
              title = "✨ 開局神賜：初始強牌";

            let desc1 = "你成功擊敗了敵人！作為獎勵，";
            if (cardRewardContext === "levelup")
              desc1 = "你選擇了進階卡牌獎勵！";
            if (cardRewardContext === "initial")
              desc1 = "冒險即將開始！神明賜予你一份力量，";

            return (
              <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50 backdrop-blur-md overflow-y-auto">
                <div className="bg-slate-800 p-8 rounded-2xl border-2 border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.5)] max-w-5xl w-full mx-4 text-center my-8">
                  <div className="text-6xl mb-4">🎁</div>
                  <h2 className="text-3xl font-bold mb-4 text-blue-400 tracking-wider">
                    {title}
                  </h2>

                  <div className="bg-slate-900/60 border border-slate-700 p-5 rounded-xl mb-8 inline-block shadow-inner">
                    <p className="text-slate-200 text-lg leading-relaxed">
                      {desc1}請從下列稀有的卡牌中挑選{" "}
                      <span className="text-yellow-400 font-bold text-2xl mx-1">
                        最多 {rewardLimit} 張
                      </span>{" "}
                      增強你的牌庫。
                    </p>
                    <p className="text-sm text-slate-400 mt-2">
                      (點擊下方卡牌即可選取或取消。若不需要新卡牌，也可以直接點擊最下方的按鈕跳過)
                    </p>
                    <div className="text-blue-400 font-bold mt-3 text-xl bg-blue-900/30 py-2 rounded-lg border border-blue-800/50">
                      目前已選取：{selectedRewardCards.length} / {rewardLimit}
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-center gap-6 mb-8 min-h-[14rem]">
                    {rewardCards.map((card) => {
                      const isSelected = selectedRewardCards.some(
                        (c) => c.id === card.id
                      );
                      return (
                        <div key={card.id} className="relative">
                          {renderCard(
                            card,
                            toggleRewardCard,
                            false,
                            isSelected
                          )}
                          {isSelected && (
                            <div className="absolute -top-3 -right-3 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center border-2 border-slate-900 font-bold shadow-lg z-10">
                              ✓
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {rewardCards.length === 0 && (
                      <div className="text-slate-400 py-10 w-full text-center">
                        沒有足夠的中高級/高級卡牌可供選擇。
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center gap-4">
                    <button
                      onClick={confirmCardReward}
                      className={`px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg flex items-center gap-2 ${
                        selectedRewardCards.length > 0
                          ? "bg-green-600 hover:bg-green-500 text-white ring-4 ring-green-600/50"
                          : "bg-slate-700 hover:bg-slate-600 text-slate-300"
                      }`}
                    >
                      {selectedRewardCards.length > 0
                        ? "✅ 確認選擇並加入牌庫"
                        : "⏭️ 不需要，直接跳過獎勵"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

        {gameState === "CARD_REMOVAL" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50 backdrop-blur-md overflow-y-auto">
            <div className="bg-slate-900 p-8 rounded-2xl border-2 border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.5)] max-w-6xl w-full mx-4 text-center my-8">
              <div className="text-6xl mb-4 animate-pulse">🔥</div>
              <h2 className="text-3xl font-bold mb-2 text-red-400 tracking-wider">
                精簡牌庫
              </h2>
              <p className="text-slate-300 mb-8">
                為了讓牌組運轉更順暢，你可以選擇{" "}
                <span className="text-red-400 font-bold">
                  最多 {removalLimit} 張
                </span>{" "}
                卡牌從牌庫與手牌中永久消除。 ({removalSelected.length}/
                {removalLimit})
              </p>

              <div className="flex flex-wrap justify-center gap-6 mb-8 max-h-[50vh] overflow-y-auto custom-scrollbar p-2">
                {[...hand, ...deck, ...discardPile].map((card) => {
                  const isSelected = removalSelected.includes(card.uid);
                  return (
                    <div key={card.uid} className="relative">
                      {renderCard(
                        card,
                        () => toggleRemovalCard(card.uid),
                        false,
                        isSelected,
                        true
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl z-10 pointer-events-none">
                          <X
                            size={64}
                            className="text-red-500 drop-shadow-lg"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={confirmCardRemoval}
                  className={`px-8 py-3 rounded-xl font-bold text-lg transition-all shadow-lg ${
                    removalSelected.length > 0
                      ? "bg-red-600 hover:bg-red-500 text-white"
                      : "bg-slate-600 hover:bg-slate-500 text-white"
                  }`}
                >
                  {removalSelected.length > 0
                    ? "確認消除選中卡牌"
                    : "不消除任何卡牌"}
                </button>
              </div>
            </div>
          </div>
        )}

        {gameState === "QUESTION" && currentQuestion && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-40 backdrop-blur-md">
            <div className="bg-slate-800 p-8 rounded-2xl border-2 border-yellow-500 shadow-[0_0_30px_rgba(234,179,8,0.3)] max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <span className="bg-yellow-600 text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                  {currentQuestion.targetTier &&
                  currentQuestion.targetTier !== activeCard?.tier ? (
                    <span className="text-red-200">
                      {activeCard?.tier} ➔ {currentQuestion.targetTier} 題目
                      (題庫竄改)
                    </span>
                  ) : (
                    <span>{activeCard?.tier} 題目</span>
                  )}
                </span>
                <span className="text-slate-400">卡牌發動確認</span>
              </div>
              <h2 className="text-2xl font-bold mb-8 leading-relaxed text-white">
                {currentQuestion.text}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentQuestion.options.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(opt)}
                    className="bg-slate-700 hover:bg-blue-600 border border-slate-600 hover:border-blue-400 text-white font-semibold py-4 px-6 rounded-xl text-left transition-colors flex items-center gap-4 text-lg"
                  >
                    <span className="bg-slate-900 w-8 h-8 rounded-full flex items-center justify-center text-sm text-slate-400">
                      {["A", "B", "C", "D"][idx]}
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {(gameState === "BATTLE" ||
          gameState === "QUESTION" ||
          gameState === "ACTION_RESULT") && (
          <>
            <div className="flex-1 flex flex-col gap-4">
              <div className="flex-1 bg-slate-900/50 rounded-2xl border border-slate-800 flex flex-col items-center justify-center relative p-6">
                <h2 className="text-xl font-bold text-red-400 mb-2">
                  {enemies[currentEnemyIdx]?.name}
                </h2>

                {/* 顯示前綴技能 */}
                {enemies[currentEnemyIdx]?.prefix && (
                  <div className="text-xs font-bold text-white bg-red-950 px-3 py-1 rounded-full border border-red-500 mb-3 shadow-md">
                    {enemies[currentEnemyIdx].prefix === "可愛的" &&
                      "🎀 [可愛的] 敵人太可愛！你的初/中級攻擊牌傷害減半。"}
                    {enemies[currentEnemyIdx].prefix === "超級可愛" &&
                      "🎀 [超級可愛] 魔王萌翻天！你的初/中級卡牌所有效果減半。"}
                    {enemies[currentEnemyIdx].prefix === "愛吃的" &&
                      `🍖 [愛吃的] 敵人胃口大開！每回合吞噬 1 張初/中級卡牌的效果。(已吞噬: ${
                        enemies[currentEnemyIdx].eatenCount || 0
                      }/1)`}
                    {enemies[currentEnemyIdx].prefix === "貪吃的" &&
                      `🍖 [貪吃的] 魔王暴食！每回合吞噬 2 張初/中級卡牌的效果。(已吞噬: ${
                        enemies[currentEnemyIdx].eatenCount || 0
                      }/2)`}
                    {enemies[currentEnemyIdx].prefix === "吹牛的" &&
                      "💨 [吹牛的] 敵人胡言亂語！每回合隨機 2 張初/中級卡牌耗魔 +1。"}
                    {enemies[currentEnemyIdx].prefix === "放羊小孩的" &&
                      "🐺 [放羊小孩的] 魔王謊話連篇！所有初/中級卡牌耗魔 +1。"}
                    {enemies[currentEnemyIdx].prefix === "迷糊的" &&
                      "😵‍💫 [迷糊的] 敵人搞錯了！初級或中級卡牌 (隨機一種) 將面臨更難的題目。"}
                    {enemies[currentEnemyIdx].prefix === "混亂的" &&
                      "🌀 [混亂的] 魔王扭曲了現實！初級面臨中高級題目，中級面臨高級題目。"}
                  </div>
                )}

                <div className="w-full max-w-md bg-slate-800 rounded-full h-4 mb-2">
                  <div
                    className="bg-red-500 h-4 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        (enemies[currentEnemyIdx]?.hp /
                          enemies[currentEnemyIdx]?.maxHp) *
                        100
                      }%`,
                    }}
                  ></div>
                </div>
                <div className="text-sm text-slate-400 mb-6">
                  HP: {enemies[currentEnemyIdx]?.hp} /{" "}
                  {enemies[currentEnemyIdx]?.maxHp} | 攻擊力:{" "}
                  {enemies[currentEnemyIdx]?.atk}
                </div>
                <div
                  className={`rounded-full flex items-center justify-center border-4 relative transition-all duration-150 shadow-xl ${
                    enemies[currentEnemyIdx]?.type === "boss"
                      ? "bg-red-950/50 border-red-500 w-48 h-48"
                      : "bg-slate-800 border-slate-600 w-32 h-32"
                  } ${
                    enemyHitState
                      ? "animate-shake border-red-400 bg-red-900"
                      : ""
                  }`}
                >
                  <div className={`${enemyHitState ? "" : "animate-breathe"}`}>
                    <span
                      className={`drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] ${
                        enemies[currentEnemyIdx]?.type === "boss"
                          ? "text-[6rem]"
                          : "text-[4rem]"
                      }`}
                    >
                      {getEnemyAvatar(enemies[currentEnemyIdx]?.id)}
                    </span>
                  </div>
                  {floatingTexts
                    .filter((t) => t.target === "enemy")
                    .map((t) => (
                      <div
                        key={t.id}
                        className={`absolute text-5xl font-black z-50 ${t.color} animate-float-damage pointer-events-none drop-shadow-md`}
                      >
                        {t.text}
                      </div>
                    ))}
                </div>
              </div>

              <div
                className={`h-32 bg-slate-900/50 rounded-2xl border border-slate-800 flex justify-between items-center p-6 relative transition-all duration-150 ${
                  playerHitState
                    ? "animate-shake border-red-500 bg-red-950/80"
                    : ""
                }`}
              >
                {floatingTexts
                  .filter((t) => t.target === "player")
                  .map((t) => (
                    <div
                      key={t.id}
                      className={`absolute left-1/3 top-0 text-4xl font-black z-50 ${t.color} animate-float-damage pointer-events-none drop-shadow-md`}
                    >
                      {t.text}
                    </div>
                  ))}
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-blue-400 mb-2 flex items-center gap-2 flex-wrap">
                    玩家 ({playerClass}
                    {player.extraClasses?.length > 0
                      ? ` + ${player.extraClasses.join(" & ")}`
                      : ""}
                    )
                    <span className="text-xs bg-yellow-600/20 text-yellow-500 border border-yellow-600/50 px-2 py-1 rounded-md font-bold flex items-center gap-1">
                      Lv.{player.level} (答對 {player.correctCount}/
                      {player.level * 10})
                    </span>
                    {player.passives.comboDamage > 0 && (
                      <span
                        className={`text-xs px-2 py-1 rounded-md font-bold ${
                          player.comboCount > 0
                            ? "bg-orange-600/20 text-orange-400 border border-orange-600/50"
                            : "bg-slate-700/50 text-slate-400 border border-slate-600"
                        }`}
                      >
                        連擊加成:{" "}
                        {player.comboCount > 0
                          ? `+${
                              player.comboCount *
                              5 *
                              player.passives.comboDamage
                            }%`
                          : "無"}
                      </span>
                    )}
                    {player.revengeActive && (
                      <span className="text-xs bg-rose-600/20 text-rose-400 border border-rose-600/50 px-2 py-1 rounded-md font-bold animate-pulse">
                        復仇就緒 (下次攻擊+${player.passives.revenge * 50}%)
                      </span>
                    )}
                  </h2>
                  <div className="w-full max-w-sm bg-slate-800 rounded-full h-4 mb-1">
                    <div
                      className="bg-green-500 h-4 rounded-full transition-all duration-500"
                      style={{ width: `${(player.hp / player.maxHp) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex gap-4 text-sm font-bold">
                    <span className="text-green-400 flex items-center gap-1">
                      <Heart size={14} /> {player.hp}/{player.maxHp}
                    </span>
                    <span className="text-blue-400 flex items-center gap-1">
                      <Shield size={14} /> {player.shield} 護盾
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-sm text-purple-400 font-bold mb-1 flex items-center gap-1">
                    <Zap size={16} /> 法力值
                  </span>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {[...Array(Math.max(player.maxMana, player.mana))].map(
                      (_, i) => {
                        const isTemp = i >= player.maxMana;
                        const hasMana = i < player.mana;
                        return (
                          <div
                            key={i}
                            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                              isTemp
                                ? "border-cyan-400 bg-cyan-500 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                                : hasMana
                                ? "border-purple-500 bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.8)]"
                                : "border-purple-500 bg-transparent"
                            }`}
                          >
                            {hasMana && (
                              <Zap size={16} className="text-white" />
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>

              <div
                className={`bg-slate-900 rounded-2xl border border-slate-800 p-4 transition-all duration-300 ${
                  isTutorial && tutorialStep === 2
                    ? "ring-4 ring-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.5)] relative z-10"
                    : ""
                }`}
              >
                {gameState === "ACTION_RESULT" ? (
                  <div className="flex flex-col items-center justify-center py-6 min-h-[16rem]">
                    <h3 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center gap-2">
                      <BookOpen size={28} /> 戰鬥結算
                    </h3>
                    <div className="space-y-3 w-full max-w-2xl mb-8 flex flex-col items-center">
                      {currentActionLogs.map((log, idx) => {
                        let colorClass = "text-white";
                        if (
                          log.includes("✅") ||
                          log.includes("💚") ||
                          log.includes("🎀") ||
                          log.includes("🏅")
                        )
                          colorClass = "text-green-400 font-bold";
                        if (
                          log.includes("❌") ||
                          log.includes("⚠️") ||
                          log.includes("😈") ||
                          log.includes("🐺")
                        )
                          colorClass = "text-red-400 font-bold";
                        if (log.includes("💡"))
                          colorClass = "text-slate-300 text-sm font-normal";
                        if (log.includes("🛡️") || log.includes("📜"))
                          colorClass = "text-blue-300";
                        if (log.includes("⚡") || log.includes("🌟"))
                          colorClass = "text-purple-300";
                        if (log.includes("🔥")) colorClass = "text-orange-400";
                        return (
                          <div
                            key={idx}
                            className={`bg-slate-800 border border-slate-700 px-6 py-4 rounded-xl w-full text-center shadow-sm text-lg ${colorClass}`}
                          >
                            {log}
                          </div>
                        );
                      })}
                    </div>
                    <button
                      onClick={proceedFromActionResult}
                      className="bg-blue-600 hover:bg-blue-500 px-10 py-3 rounded-xl font-bold text-xl text-white shadow-[0_0_15px_rgba(37,99,235,0.5)] transition-transform hover:scale-105 flex items-center gap-2"
                    >
                      下一步 <ArrowRight size={24} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4 px-2">
                      <h3 className="font-bold text-slate-300">
                        手牌 ({hand.length}/{player.drawCount}) - 牌庫:{" "}
                        {deck.length} - 棄牌堆: {discardPile.length}
                      </h3>
                      <button
                        onClick={endTurn}
                        className="bg-red-900 hover:bg-red-800 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 border border-red-700 transition-colors"
                      >
                        結束回合 <ArrowRight size={16} />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center min-h-[14rem]">
                      {hand.map((card) =>
                        renderCard(card, playCard, player.mana < card.cost)
                      )}
                      {hand.length === 0 && (
                        <div className="w-full flex items-center justify-center text-slate-500">
                          手中沒有卡牌了，請結束回合。
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
